import React from "react";
import {
  Dialog,
  DialogContent,
  Typography,
  Box,
  Button,
  IconButton,
  Icon,
  alpha,
  Slide,
  CircularProgress,
} from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

interface LogoutDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isLoading?: boolean;
}

export const LogoutDialog: React.FC<LogoutDialogProps> = ({
  open,
  onClose,
  onConfirm,
  isLoading = false,
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
      PaperProps={{
        sx: {
          borderRadius: 2,
          width: "100%",
          maxWidth: 400,
          overflow: "hidden",
        },
      }}
    >
      <Box
        sx={{
          position: "relative",
          p: 3,
          background: (theme) =>
            `linear-gradient(45deg, ${alpha(
              theme.palette.primary.main,
              0.05
            )}, ${alpha(theme.palette.secondary.main, 0.05)})`,
        }}
      >
        <IconButton
          onClick={onClose}
          disabled={isLoading}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "text.secondary",
            transition: "all 0.2s",
            "&:hover": {
              transform: "rotate(90deg)",
              color: "text.primary",
            },
          }}
        >
          <Icon>close</Icon>
        </IconButton>
        <Box sx={{ textAlign: "center", mb: 2 }}>
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: "50%",
              bgcolor: (theme) => alpha(theme.palette.error.main, 0.1),
              color: "error.main",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto",
              mb: 2,
              position: "relative",
              "&::after": {
                content: '""',
                position: "absolute",
                inset: -4,
                borderRadius: "50%",
                border: "2px solid",
                borderColor: (theme) => alpha(theme.palette.error.main, 0.2),
              },
            }}
          >
            <Icon
              sx={{
                fontSize: 40,
                animation: isLoading ? "spin 2s linear infinite" : "none",
              }}
            >
              {isLoading ? "sync" : "logout"}
            </Icon>
          </Box>
          <Typography
            variant="h6"
            component="div"
            sx={{ mb: 1, fontWeight: 600 }}
          >
            {isLoading ? "Logging Out..." : "Confirm Logout"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isLoading
              ? "Please wait while we securely log you out."
              : "Are you sure you want to log out? You will need to log in again to access your account."}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 2, mt: 3 }}>
          <Button
            fullWidth
            variant="outlined"
            onClick={onClose}
            disabled={isLoading}
            sx={{
              borderRadius: 1.5,
              py: 1,
              borderColor: (theme) => alpha(theme.palette.divider, 0.5),
              "&:hover": {
                borderColor: "divider",
                bgcolor: (theme) => alpha(theme.palette.divider, 0.05),
              },
            }}
          >
            Cancel
          </Button>
          <Button
            fullWidth
            variant="contained"
            color="error"
            onClick={onConfirm}
            disabled={isLoading}
            sx={{
              borderRadius: 1.5,
              py: 1,
              position: "relative",
              boxShadow: (theme) =>
                `0 4px 12px ${alpha(theme.palette.error.main, 0.3)}`,
              "&:hover": {
                boxShadow: (theme) =>
                  `0 6px 16px ${alpha(theme.palette.error.main, 0.4)}`,
                transform: "translateY(-1px)",
              },
              transition: "all 0.2s",
            }}
          >
            {isLoading ? (
              <CircularProgress
                size={20}
                color="inherit"
                sx={{
                  position: "absolute",
                  left: "50%",
                  marginLeft: "-10px",
                }}
              />
            ) : (
              "Logout"
            )}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

// Add keyframes for spin animation
const style = document.createElement("style");
style.textContent = `
  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;
document.head.appendChild(style);
